import { fmtNum } from './content';
import { mmss } from './format';
import type { ComplexFormat } from './types';

/* Результат клиента (таблица results): fact — что сделал, plan — снимок строки на момент записи.
   Строка: sets — подходы (вес, повторы, отметка); комплекс целиком: AMRAP — раунды и повторы, на время — время, Death by — раунды. */
export interface SetFact {
  kg?: number | null;
  reps?: number | null;
  done: boolean;
}
type Fact = Record<string, unknown>;
const pl = (n: number, a: string, b: string, c: string) => {
  const m = n % 10, h = n % 100;
  return n + ' ' + (h >= 11 && h <= 14 ? c : m === 1 ? a : m >= 2 && m <= 4 ? b : c);
};
const rounds = (n: unknown) => pl(Number(n), 'раунд', 'раунда', 'раундов');

/** Коротко: «4 из 4 подх. · до 140 кг», «120 кг», «5 р. + 12», «12:30», «11 из 12» */
export function factSummary(plan: { sets?: number } | null, fact: Fact | null | undefined, fmt?: ComplexFormat | null): string {
  if (!fact) return '';
  if (fmt) {
    if (fmt.k === 'AMRAP' && fact.rounds != null) return `${fact.rounds} р.` + (fact.reps ? ` + ${fact.reps}` : '');
    if (fmt.k === 'FOR_TIME' && fact.time != null) return mmss(fact.time as number);
    if (fmt.k === 'DEATH_BY' && fact.rounds != null) return `${fact.rounds} р.`;
    if ((fmt.k === 'EMOM' || fmt.k === 'ROUNDS' || fmt.k === 'INTERVALS') && fact.rounds != null) return `${fact.rounds} из ${fmt.rounds}`;
    if (fmt.k === 'TABATA' && fact.reps != null) return `${fact.reps} повт.`;
    return '';
  }
  /* Вкладка «Итог»: вес строки целиком */
  if (typeof fact.kg === 'number') return fmtNum(fact.kg) + ' кг';
  const sets = (fact.sets as SetFact[] | undefined) || [];
  const done = sets.filter((s) => s.done).length;
  if (!sets.length) return '';
  const kgs = sets.filter((s) => s.done && s.kg != null).map((s) => s.kg as number);
  const kg = kgs.length ? Math.max(...kgs) : null;
  return `${done} из ${Math.max(plan?.sets || 0, sets.length)} подх.` + (kg != null ? ` · до ${fmtNum(kg)} кг` : '');
}

/** Для тренера — по подходам: «140×3, 140×3, 140×2»; комплекс — как в сводке, словами */
export function factDetail(plan: { sets?: number } | null, fact: Fact | null | undefined, fmt?: ComplexFormat | null): string {
  if (!fact) return '';
  if (fmt) {
    if (fmt.k === 'AMRAP' && fact.rounds != null) return rounds(fact.rounds) + (fact.reps ? ' + ' + pl(Number(fact.reps), 'повтор', 'повтора', 'повторов') : '');
    if (fmt.k === 'FOR_TIME' && fact.time != null) return 'время ' + mmss(fact.time as number);
    if (fmt.k === 'DEATH_BY' && fact.rounds != null) return rounds(fact.rounds);
    if (fmt.k === 'EMOM' || fmt.k === 'ROUNDS') return fact.rounds != null ? `${fact.rounds} из ${pl(fmt.rounds, 'раунда', 'раундов', 'раундов').replace(/^\d+ /, fmt.rounds + ' ')}` : '';
    if (fmt.k === 'INTERVALS') return fact.rounds != null ? `${fact.rounds} из ${pl(fmt.rounds, 'интервала', 'интервалов', 'интервалов')}` : '';
    if (fmt.k === 'TABATA' && fact.reps != null) return pl(Number(fact.reps), 'повтор', 'повтора', 'повторов') + ' всего';
    return '';
  }
  if (typeof fact.kg === 'number') return fmtNum(fact.kg) + ' кг';
  const unit = typeof fact.unit === 'string' ? ' ' + fact.unit : '';
  const sets = ((fact.sets as SetFact[] | undefined) || []).filter((s) => s.done);
  const parts = sets.map((s) => (s.kg != null ? fmtNum(s.kg) + (s.reps != null ? '×' + fmtNum(s.reps) : ' кг') : s.reps != null ? fmtNum(s.reps) + unit : '✓'));
  const total = Math.max(plan?.sets || 0, ((fact.sets as SetFact[] | undefined) || []).length);
  if (!parts.length) return total ? `0 из ${total} подх.` : '';
  return (sets.length < total ? `${sets.length} из ${total} подх.: ` : '') + parts.join(', ');
}
